import React, { useEffect, useMemo, useState } from "react";
import api, { fmtInt, fmtPct } from "@/api";
import { useI18n } from "@/i18n";
import { useCurrency } from "@/currency";
import { PageHeader, Tile, Loading, StatusPill, OperatorLink } from "@/components/Bits";
import { CommentsThread, ExportColumnsModal } from "@/components/Extras";
import { OutcomeBadge } from "@/components/Outcomes";
import ImportExportBar from "@/components/ImportExportBar";
import { useCountry } from "@/country";
import { useShowPrices } from "@/showPrices";
import { Sliders, ChatCircleDots, DownloadSimple, Info, CaretDown } from "@phosphor-icons/react";

const STATUSES = ["Hot", "Warm", "Cold", "Past/Now", "Unknown"];
const METHODS = [
  { v: 1, k: "table.m1", label: "Theoretical" },
  { v: 2, k: "table.m2", label: "Addressable" },
  { v: 3, k: "table.m3", label: "Weighted" },
];

export default function Opportunities() {
  const { t, lang } = useI18n();
  const { fmt: fmtMoney, currency } = useCurrency();
  const { code: country } = useCountry();
  const { showPrices } = useShowPrices();
  const [ref, setRef] = useState(null);
  const [rows, setRows] = useState(null);
  const [filters, setFilters] = useState(() => {
    // Pre-filter support: /opportunities?operator=X&city=Y (used by the operator 360° page)
    const p = new URLSearchParams(window.location.search);
    const init = {};
    for (const k of ["city", "operator", "sub_system", "ovh_status"]) {
      if (p.get(k)) init[k] = p.get(k);
    }
    return init;
  });
  const [method, setMethod] = useState(3);
  const [whatifOpen, setWhatifOpen] = useState(false);
  const [whatifBody, setWhatifBody] = useState({
    method_switch: 3,
    prob_wabtec: 0.6,
    prob_tbc: 0.3,
    prob_competitor: 0.15,
    timing_now: 1,
    timing_short: 0.85,
    timing_mid: 0.55,
    timing_long: 0.25,
    current_year: 2026,
  });
  const [whatifResult, setWhatifResult] = useState(null);
  const [whatifLoading, setWhatifLoading] = useState(false);
  const [commentsFor, setCommentsFor] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [outcomeMap, setOutcomeMap] = useState({});

  useEffect(() => {
    api.get("/reference").then((r) => setRef(r.data));
    // One batched call for all outcomes instead of one request per row.
    api.get("/outcomes").then((r) => {
      const map = {};
      for (const o of r.data.rows || []) map[o.opp_key] = o;
      setOutcomeMap(map);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const params = { method };
    for (const k of ["city", "operator", "sub_system", "ovh_status"]) {
      if (filters[k]) params[k] = filters[k];
    }
    api.get("/opportunities", { params }).then((r) => setRows(r.data.rows));
  }, [filters, method]);

  const totals = useMemo(() => {
    if (!rows) return null;
    let m1 = 0, m2 = 0, m3 = 0, active = 0;
    for (const r of rows) {
      m1 += r.m1_theoretical || 0;
      m2 += r.m2_addressable || 0;
      m3 += r.m3_weighted || 0;
      active += r.active_method || 0;
    }
    return { m1, m2, m3, active };
  }, [rows]);

  const runWhatIf = async () => {
    setWhatifLoading(true);
    try {
      const { data } = await api.post("/whatif", whatifBody);
      setWhatifResult(data);
    } finally {
      setWhatifLoading(false);
    }
  };

  const resetWhatIf = () => {
    setWhatifBody({
      method_switch: 3,
      prob_wabtec: 0.6,
      prob_tbc: 0.3,
      prob_competitor: 0.15,
      timing_now: 1,
      timing_short: 0.85,
      timing_mid: 0.55,
      timing_long: 0.25,
      current_year: 2026,
    });
    setWhatifResult(null);
  };

  if (!ref) return <Loading />;

  const FilterSel = ({ k, opts, label }) => (
    <div>
      <label className="label-tech block mb-1.5">{label}</label>
      <select
        value={filters[k] || ""}
        onChange={(e) => setFilters((f) => ({ ...f, [k]: e.target.value || undefined }))}
        className="input-tech"
        data-testid={`opp-filter-${k}`}
      >
        <option value="">{t("filters.all")}</option>
        {opts.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );

  const SliderRow = ({ label, k, min, max, step }) => (
    <div className="grid grid-cols-[1fr_auto] gap-3 items-center py-1.5">
      <div>
        <div className="text-xs text-zinc-300">{label}</div>
        <input
          type="range"
          min={min} max={max} step={step}
          value={whatifBody[k]}
          onChange={(e) => setWhatifBody((b) => ({ ...b, [k]: parseFloat(e.target.value) }))}
          className="w-full accent-wabtec-red"
          data-testid={`whatif-${k}`}
        />
      </div>
      <div className="num text-sm w-16 text-right">{Number(whatifBody[k]).toFixed(2)}</div>
    </div>
  );

  return (
    <div data-testid="opportunities-page">
      <PageHeader
        title={t("nav.opportunities")}
        subtitle={t("opp.subtitle")}
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setExportOpen(true)}
              className="btn-ghost flex items-center gap-2"
              data-testid="opp-export"
            >
              <DownloadSimple size={14} />
              {t("export.button")}
            </button>
            <ImportExportBar
              entity="opportunities"
              country={country}
              canImport={false}
              exportOnly={true}
            />
            <button
              onClick={() => setWhatifOpen((v) => !v)}
              className="btn-accent flex items-center gap-2"
              data-testid="whatif-toggle"
              style={{ display: showPrices ? undefined : "none" }}
            >
              <Sliders size={14} weight="regular" />
              {t("whatif.title")}
            </button>
          </div>
        }
      />
      <div className="p-4 md:p-8 space-y-4 md:space-y-5">
        {/* Method tabs (only meaningful when prices are visible) */}
        {showPrices && <MethodsExplainer t={t} lang={lang} />}

        {showPrices && (
          <div className="tile p-2 inline-flex" data-testid="method-tabs">
            {METHODS.map((m) => (
              <button
                key={m.v}
                onClick={() => setMethod(m.v)}
                data-testid={`method-${m.v}`}
                className={`px-4 py-2 text-xs font-mono uppercase tracking-wider transition-colors ${
                  method === m.v ? "bg-wabtec-red text-white" : "text-zinc-400 hover:text-white"
                }`}
              >
                M{m.v} · {m.label}
              </button>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <FilterSel k="city" opts={ref.cities} label={t("filters.city")} />
          <FilterSel k="operator" opts={ref.operators} label={t("filters.operator")} />
          <FilterSel k="sub_system" opts={ref.sub_systems} label={t("filters.sub")} />
          <FilterSel k="ovh_status" opts={STATUSES} label={t("filters.status")} />
          <div className="flex items-end">
            <button onClick={() => setFilters({})} className="btn-ghost w-full" data-testid="opp-clear">
              {t("filters.clear")}
            </button>
          </div>
        </div>

        {/* Totals */}
        {totals && (
          showPrices ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Tile testId="tot-m1"><div className="label-tech">{t("pipeline.m1")}</div><div className="num text-2xl mt-1">{fmtMoney(totals.m1)}</div></Tile>
              <Tile testId="tot-m2"><div className="label-tech">{t("pipeline.m2")}</div><div className="num text-2xl mt-1">{fmtMoney(totals.m2)}</div></Tile>
              <Tile testId="tot-m3"><div className="label-tech">{t("pipeline.m3")}</div><div className="num text-2xl mt-1">{fmtMoney(totals.m3)}</div></Tile>
              <Tile testId="tot-active"><div className="label-tech">{t("pipeline.active")}</div><div className="num text-2xl mt-1 text-wabtec-red">{fmtMoney(totals.active)}</div></Tile>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Tile testId="tot-opps">
                <div className="label-tech">{lang === "fr" ? "Opportunités" : "Opportunities"}</div>
                <div className="num text-2xl mt-1">{fmtInt(rows?.length || 0)}</div>
              </Tile>
              <Tile testId="tot-qty">
                <div className="label-tech">{lang === "fr" ? "Équipements totaux" : "Total equipment"}</div>
                <div className="num text-2xl mt-1">{fmtInt((rows || []).reduce((s, r) => s + (r.total_qty || 0), 0))}</div>
              </Tile>
              <Tile testId="tot-hot">
                <div className="label-tech">{lang === "fr" ? "Past/Now + Hot" : "Past/Now + Hot"}</div>
                <div className="num text-2xl mt-1 text-wabtec-red">{fmtInt((rows || []).filter((r) => r.ovh_status === "Past/Now" || r.ovh_status === "Hot").length)}</div>
              </Tile>
              <Tile testId="tot-operators">
                <div className="label-tech">{lang === "fr" ? "Opérateurs uniques" : "Unique operators"}</div>
                <div className="num text-2xl mt-1">{new Set((rows || []).map((r) => r.operator)).size}</div>
              </Tile>
            </div>
          )
        )}

        {/* What-if panel */}
        {whatifOpen && (
          <Tile testId="whatif-panel">
            <div className="label-tech mb-2 flex items-center gap-2">
              <Sliders size={14} /> {t("whatif.title")}
            </div>
            <div className="text-[12.5px] mb-4 p-3 rounded-[10px]" style={{ background: "var(--bg-subtle)", color: "var(--text-secondary)" }} data-testid="whatif-hint">
              {lang === "fr"
                ? "🎚️ Réglez les probabilités de gain (par scénario fournisseur) et les coefficients de timing (par bucket OVH), puis cliquez sur Recalculer pour voir l'impact instantané sur le pipeline M3 sans modifier les données."
                : "🎚️ Tune the win probabilities (per supplier scenario) and timing coefficients (per OVH bucket), then click Recompute to see the live impact on the M3 pipeline without altering the data."}
              <button onClick={() => { const el = document.querySelector('[data-testid="assistant-open"]'); el && el.click(); }} className="ml-2 underline" style={{ color: "var(--accent)" }}>
                {lang === "fr" ? "Demander à l'IA" : "Ask the AI"}
              </button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div>
                <div className="text-xs text-zinc-400 mb-2">{t("whatif.method")}</div>
                <div className="flex gap-1.5 mb-4">
                  {METHODS.map((m) => (
                    <button
                      key={m.v}
                      onClick={() => setWhatifBody((b) => ({ ...b, method_switch: m.v }))}
                      className={`px-3 py-1.5 text-xs font-mono ${
                        whatifBody.method_switch === m.v
                          ? "bg-wabtec-red text-white"
                          : "border border-white/15 text-zinc-300 hover:bg-white/5"
                      }`}
                      data-testid={`whatif-method-${m.v}`}
                    >
                      M{m.v}
                    </button>
                  ))}
                </div>
                <SliderRow label={t("whatif.probWabtec")} k="prob_wabtec" min={0} max={1} step={0.05} />
                <SliderRow label={t("whatif.probTbc")} k="prob_tbc" min={0} max={1} step={0.05} />
                <SliderRow label={t("whatif.probComp")} k="prob_competitor" min={0} max={1} step={0.05} />
              </div>
              <div>
                <SliderRow label={t("whatif.timingNow")} k="timing_now" min={0} max={1} step={0.05} />
                <SliderRow label={t("whatif.timingShort")} k="timing_short" min={0} max={1} step={0.05} />
                <SliderRow label={t("whatif.timingMid")} k="timing_mid" min={0} max={1} step={0.05} />
                <SliderRow label={t("whatif.timingLong")} k="timing_long" min={0} max={1} step={0.05} />
                <div className="flex gap-2 mt-4">
                  <button onClick={runWhatIf} disabled={whatifLoading} className="btn-primary flex-1" data-testid="whatif-run">
                    {whatifLoading ? t("common.loading") : t("whatif.run")}
                  </button>
                  <button onClick={resetWhatIf} className="btn-ghost" data-testid="whatif-reset">
                    {t("whatif.reset")}
                  </button>
                </div>
              </div>
              <div>
                <div className="label-tech mb-3">{t("whatif.result")}</div>
                {whatifResult ? (
                  <div className="space-y-2">
                    <div className="flex justify-between"><span className="text-xs text-zinc-400">{t("pipeline.m1")}</span><span className="num text-sm">{fmtMoney(whatifResult.totals.m1_theoretical)}</span></div>
                    <div className="flex justify-between"><span className="text-xs text-zinc-400">{t("pipeline.m2")}</span><span className="num text-sm">{fmtMoney(whatifResult.totals.m2_addressable)}</span></div>
                    <div className="flex justify-between border-t border-white/10 pt-2"><span className="text-xs text-zinc-300">{t("pipeline.m3")}</span><span className="num text-base text-wabtec-red">{fmtMoney(whatifResult.totals.m3_weighted)}</span></div>
                    {totals && (
                      <div className="text-xs text-zinc-500 mt-3 font-mono">
                        Δ M3 = {fmtMoney(whatifResult.totals.m3_weighted - totals.m3)}
                      </div>
                    )}
                    <div className="mt-4 grid grid-cols-5 gap-1 text-center">
                      {Object.entries(whatifResult.by_status).map(([k, v]) => (
                        <div key={k} className="border border-white/10 p-1.5">
                          <div className="text-[9px] uppercase text-zinc-500 tracking-wider">{k}</div>
                          <div className="num text-xs mt-0.5">{v}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-zinc-500 font-mono">Hit "{t("whatif.run")}"</div>
                )}
              </div>
            </div>
          </Tile>
        )}

        {/* Table */}
        <div className="tile overflow-auto" style={{ maxHeight: "65vh" }}>
          {!rows ? (
            <Loading />
          ) : (
            <table className="tech" data-testid="opp-table">
              <thead>
                <tr>
                  <th>{t("table.id")}</th>
                  <th>{t("table.city")}</th>
                  <th>{t("table.operator")}</th>
                  <th>{t("table.platform")}</th>
                  <th>{t("table.sub")}</th>
                  <th className="text-right">{t("table.qty")}</th>
                  <th className="text-right">{lang === "fr" ? "Échéance" : "Next OVH"}</th>
                  <th className="text-right">W%</th>
                  {showPrices && <th className="text-right">{t("table.unitPrice")}</th>}
                  {showPrices && <th className="text-right">{t("table.active")}</th>}
                  <th>{t("table.status")}</th>
                  <th>{lang === "fr" ? "Suivi" : "Outcome"}</th>
                  {showPrices && <th>{t("table.priceSource")}</th>}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 1000).map((r) => (
                  <tr key={r.id}>
                    <td className="font-mono text-xs">{r.id}</td>
                    <td>{r.city}</td>
                    <td><OperatorLink name={r.operator} /></td>
                    <td>{r.platform}</td>
                    <td>{r.sub_system}</td>
                    <td className="num">{fmtInt(r.total_qty)}</td>
                    <td className="num font-mono text-xs">{r.next_ovh || "—"}</td>
                    <td className="num text-zinc-400">{fmtPct((r.wabtec_share || 0) * 100)}</td>
                    {showPrices && <td className="num">{r.unit_price ? fmtMoney(r.unit_price) : "—"}</td>}
                    {showPrices && <td className="num text-wabtec-red">{r.active_method ? fmtMoney(r.active_method) : "—"}</td>}
                    <td><StatusPill status={r.ovh_status} /></td>
                    <td><OutcomeBadge opp={r} lang={lang} outcome={outcomeMap[r.opp_key]}
                                      onChange={(o) => setOutcomeMap((m) => ({ ...m, [r.opp_key]: o }))} /></td>
                    {showPrices && <td className="text-xs font-mono text-zinc-500">{r.price_source}</td>}
                    <td className="text-right">
                      <button
                        onClick={() => setCommentsFor(r.opp_key || r.id)}
                        className="p-1.5 rounded-full hover:bg-[var(--bg-subtle)]"
                        data-testid={`opp-comments-${r.id}`}
                        title={t("comments.open")}
                      >
                        <ChatCircleDots size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {commentsFor && (
        <>
          <div className="fixed inset-0 z-40" style={{ background: "rgba(0,0,0,0.35)", backdropFilter: "blur(4px)" }} onClick={() => setCommentsFor(null)} />
          <CommentsThread
            entityType="opportunity"
            entityId={commentsFor}
            onClose={() => setCommentsFor(null)}
          />
        </>
      )}

      {exportOpen && (
        <ExportColumnsModal
          entityKey="opportunities"
          filters={filters}
          onClose={() => setExportOpen(false)}
        />
      )}
    </div>
  );
}


/* Inline, collapsible explainer card for the 3 pipeline methods. */
function MethodsExplainer({ t, lang }) {
  const [open, setOpen] = useState(false);
  const COPY = lang === "fr" ? {
    title: "Comment lire les méthodes M1 / M2 / M3 ?",
    hint: "Tap pour comprendre les 3 méthodes de calcul.",
    items: [
      { tag: "M1", name: "Théorique", desc: "qty × prix unitaire. Le potentiel BRUT du marché si on remportait tout, sans concurrent ni timing. Pour dimensionner le terrain de jeu." },
      { tag: "M2", name: "Adressable", desc: "M1 × parts non-Wabtec. La part NON encore fournie par Wabtec — votre vraie cible de conquête." },
      { tag: "M3", name: "Pondéré", desc: "M2 × probabilité × timing. L'estimation HONNÊTE de ce que vous pouvez gagner. C'est la valeur à mettre dans votre forecast." },
    ],
    ask: "Pour aller plus loin, demandez à l'assistant IA en bas à droite ↘️",
  } : {
    title: "How to read the M1 / M2 / M3 methods?",
    hint: "Tap to understand the 3 calculation methods.",
    items: [
      { tag: "M1", name: "Theoretical", desc: "qty × unit price. The RAW market potential if we won everything — no competitor, no timing. Use it to size the playing field." },
      { tag: "M2", name: "Addressable", desc: "M1 × non-Wabtec share. The portion NOT yet supplied by Wabtec — your real conquest target." },
      { tag: "M3", name: "Weighted", desc: "M2 × probability × timing. The HONEST estimate of what you can win. This is the number to put in your forecast." },
    ],
    ask: "For more, ask the AI assistant in the bottom right ↘️",
  };

  return (
    <div className="tile p-3 md:p-4" style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)" }} data-testid="methods-explainer">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2"
      >
        <div className="flex items-center gap-2 text-left">
          <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--accent)", color: "white" }}>
            <Info size={14} weight="fill" />
          </div>
          <div>
            <div className="text-[13.5px] font-semibold" style={{ color: "var(--text-primary)" }}>{COPY.title}</div>
            {!open && <div className="text-[11.5px]" style={{ color: "var(--text-tertiary)" }}>{COPY.hint}</div>}
          </div>
        </div>
        <CaretDown size={14} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 200ms" }} />
      </button>
      {open && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
          {COPY.items.map((m) => (
            <div key={m.tag} className="p-3 rounded-[10px]" style={{ background: "var(--bg-subtle)", border: "1px solid var(--border)" }}>
              <div className="flex items-baseline gap-1.5 mb-1">
                <span className="text-[15px] font-mono font-bold" style={{ color: "var(--accent)" }}>{m.tag}</span>
                <span className="text-[12.5px] font-semibold">{m.name}</span>
              </div>
              <div className="text-[12px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                {m.desc}
              </div>
            </div>
          ))}
          <div className="md:col-span-3 text-[11.5px] mt-1 font-mono" style={{ color: "var(--text-tertiary)" }}>{COPY.ask}</div>
        </div>
      )}
    </div>
  );
}
